/**
 * AutoPublish — Auto promocja (SIMPLIFIED P0.2)
 * Prosty widok: ON/OFF toggle, status, "Edytuj ustawienia"
 * Bez kalendarza, harmonogramu, historii — czysty results-based flow.
 */
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Zap, ZapOff, Facebook, Instagram, CheckCircle2,
  AlertTriangle, Loader2, Settings2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PanelLayout from '@/components/PanelLayout';
import AssistantPanel from '@/components/AssistantPanel';
import { API } from '@/utils/api';
import { GTM } from '@/utils/gtm';
import ProfileGate from '@/components/ProfileGate';

export default function AutoPublish({ embedded = false }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [fbConnected, setFbConnected] = useState(false);
  const [fbPageName, setFbPageName] = useState(null);
  const [igConnected, setIgConnected] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/booster/auto-marketing`, { headers });
      const c = res.data;
      setEnabled(c.config?.enabled || false);
      setFbConnected(c.fb_connected);
      setFbPageName(c.fb_page_name);
      setIgConnected(c.ig_connected);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleToggle = async () => {
    const newState = !enabled;
    setToggling(true);
    try {
      await axios.put(`${API}/booster/auto-marketing`, {
        enabled: newState,
        days: ['Poniedziałek', 'Środa', 'Piątek'],
        time: '18:30',
        posts_per_week: 2,
        use_ai_days: false,
      }, { headers });
      setEnabled(newState);
      if (newState) GTM.autoPublishEnabled(); else GTM.autoPublishDisabled();
      toast.success(newState ? t('autoPublish.enabledMsg') : t('autoPublish.disabledMsg'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Nie udało się zmienić ustawień');
    } finally {
      setToggling(false);
    }
  };

  if (loading) {
    return (
      <PanelLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-[#F47A3B]" />
        </div>
      </PanelLayout>
    );
  }

  const platforms = [];
  if (fbConnected) platforms.push('Facebook');
  if (igConnected) platforms.push('Instagram');
  const platformText = platforms.length > 0 ? platforms.join(' i ') : 'Facebook';

  const content = (
    <ProfileGate>
      <div className="max-w-[1200px] mx-auto" data-testid="auto-publish-page">

        {/* Header — only when standalone */}
        {!embedded && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F47A3B]/10 border border-[#F47A3B]/15 mb-4">
              <Zap className="w-3.5 h-3.5 text-[#F47A3B]" />
              <span className="text-[12px] font-semibold text-[#F47A3B]">{t('autoPublish.systemPublishes')}</span>
            </div>
            <h1 className="text-3xl font-extrabold text-zinc-900 mb-2">{t('autoPublish.autoPromo')}</h1>
            <p className="text-[15px] text-zinc-500 max-w-md mx-auto">{t('autoPublish.enableForget')}</p>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* CENTER COLUMN */}
          <div className="lg:col-span-8 space-y-5">

            {/* Main Toggle Card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl border-2 overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-all"
              style={{ borderColor: enabled ? '#10B98140' : '#E8EAED' }}
              data-testid="toggle-card"
            >
              {/* Top accent */}
              <div className="h-1.5" style={{ background: enabled ? 'linear-gradient(90deg, #10B981, #34D399)' : 'linear-gradient(90deg, #E2E8F0, #CBD5E1)' }} />

              <div className="p-6 lg:p-8">
                {/* Toggle row */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: enabled ? '#DCFCE7' : '#F1F5F9' }}>
                      {enabled
                        ? <Zap className="w-7 h-7 text-emerald-600" />
                        : <ZapOff className="w-7 h-7 text-zinc-400" />
                      }
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-zinc-900">
                        {enabled ? 'Auto promocja aktywna' : 'Auto promocja wyłączona'}
                      </h2>
                      <p className="text-[14px] text-zinc-500 mt-1 max-w-md leading-relaxed">
                        {enabled
                          ? t('autoPublish.systemAutoPublishes', { platform: platformText })
                          : t('autoPublish.enableToStart')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleToggle}
                    disabled={toggling || (!fbConnected && !enabled)}
                    className="relative w-16 h-8 rounded-full transition-colors duration-300 flex-shrink-0 mt-1"
                    style={{ background: enabled ? '#10B981' : '#CBD5E1', opacity: toggling ? 0.6 : 1 }}
                    data-testid="auto-marketing-toggle"
                  >
                    {toggling && <Loader2 className="w-4 h-4 animate-spin text-white absolute top-2 left-1/2 -translate-x-1/2" />}
                    <span
                      className="absolute top-1 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300"
                      style={{ transform: enabled ? 'translateX(34px)' : 'translateX(4px)' }}
                    />
                  </button>
                </div>

                {/* Connected platforms */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {fbConnected ? (
                    <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium bg-[#EBF5FF] text-[#1877F2] border border-[#1877F2]/15" data-testid="fb-connected-badge">
                      <Facebook className="w-3.5 h-3.5" />
                      <span>{fbPageName || 'Facebook'}</span>
                      <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium bg-[#FEF3C7] text-[#92400E] border border-[#F59E0B]/15" data-testid="fb-disconnected-badge">
                      <Facebook className="w-3.5 h-3.5" />
                      <span>Facebook</span>
                      <AlertTriangle className="w-3 h-3" />
                    </div>
                  )}
                  {igConnected ? (
                    <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium bg-[#FDE8EC] text-[#E4405F] border border-[#E4405F]/15" data-testid="ig-connected-badge">
                      <Instagram className="w-3.5 h-3.5" />
                      <span>Instagram</span>
                      <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium bg-[#F8FAFC] text-[#94A3B8] border border-[#E2E8F0]" data-testid="ig-disconnected-badge">
                      <Instagram className="w-3.5 h-3.5" />
                      <span>Instagram</span>
                      <span className="text-[10px]">wkrótce</span>
                    </div>
                  )}
                </div>

                {/* Warning if FB not connected */}
                {!fbConnected && (
                  <div className="flex items-center gap-2 p-3 rounded-xl bg-[#FEF3C7] border border-[#F59E0B]/20" data-testid="fb-warning">
                    <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <p className="text-sm text-amber-700">
                      {t('autoPublish.connectFb')}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Jak to działa — 3 premium kafelki z grafikami */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl border border-zinc-200 p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
              data-testid="status-info-card"
            >
              <div className="flex items-center gap-2 mb-5">
                <Settings2 className="w-4 h-4 text-[#F47A3B]" />
                <h3 className="text-[15px] font-bold text-zinc-900">Jak to działa</h3>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-5">
                {[
                  {
                    num: '1',
                    title: 'Przygotowuje posty',
                    desc: 'Na podstawie Twojego profilu i oferty',
                    image: 'https://static.prod-images.emergentagent.com/jobs/0a9a5c3b-4022-4a72-b3d1-5537648994d7/images/ca143da0d1016eae8afe787b8529159e0efecfc0e86a9abbf2902abb4c1928f4.png',
                  },
                  {
                    num: '2',
                    title: 'Dobiera zdjęcia',
                    desc: 'Najlepsze z Twojej galerii',
                    image: 'https://static.prod-images.emergentagent.com/jobs/0a9a5c3b-4022-4a72-b3d1-5537648994d7/images/62f839540312cb5096cddbe690e85f8b2b4fa7da7600994a4b2f34ab1ab0eb76.png',
                  },
                  {
                    num: '3',
                    title: 'Publikuje',
                    desc: `Regularnie na ${platformText}`,
                    image: 'https://static.prod-images.emergentagent.com/jobs/0a9a5c3b-4022-4a72-b3d1-5537648994d7/images/4903132e0c7fc62e4bc3d6102d159e6445e52e519afccd4062631f96f7c04a3a.png',
                  },
                ].map((step, i) => (
                  <motion.div
                    key={step.num}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: 0.15 + i * 0.1 }}
                    className="text-center group"
                  >
                    <div className="relative w-full aspect-square rounded-2xl overflow-hidden mb-3 border border-[#F47A3B]/15 shadow-[0_4px_20px_rgba(244,122,59,0.08)] group-hover:shadow-[0_8px_30px_rgba(244,122,59,0.15)] group-hover:border-[#F47A3B]/30 transition-all duration-300">
                      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-[#F47A3B]/40 to-transparent" />
                      <div className="absolute inset-0 bg-gradient-to-b from-[#FFF8F3] to-white" />
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#F47A3B] flex items-center justify-center text-[10px] font-bold text-white shadow-md z-10">
                        {step.num}
                      </div>
                      <div className="relative w-full h-full flex items-center justify-center p-5">
                        <img src={step.image} alt={step.title} className="w-full h-full object-contain drop-shadow-sm group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                      </div>
                    </div>
                    <h4 className="text-[13px] font-bold text-zinc-900 mb-0.5">{step.title}</h4>
                    <p className="text-[11px] text-zinc-500">{step.desc}</p>
                  </motion.div>
                ))}
              </div>

              <div className="pt-4 border-t border-zinc-200">
                <Button
                  variant="outline"
                  className="rounded-xl text-[13px] font-medium text-zinc-500 border-zinc-200 hover:border-[#F47A3B]/30 hover:text-[#F47A3B]"
                  onClick={() => toast.info('Zaawansowane ustawienia będą dostępne wkrótce')}
                  data-testid="edit-settings-btn"
                >
                  <Settings2 className="w-4 h-4 mr-2" />
                  Edytuj ustawienia
                </Button>
              </div>
            </motion.div>
          </div>

          {/* RIGHT COLUMN — Assistant */}
          <motion.div
            className="lg:col-span-4"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15 }}
            data-testid="auto-publish-right-column"
          >
            <div className="sticky top-[80px]">
              <AssistantPanel
                title={t('autoPublish.autoPromo')}
                whatNow={enabled
                  ? t('autoPublish.contextActiveDesc')
                  : t('autoPublish.contextInactiveDesc')}
                checklist={[
                  { label: 'Facebook połączony', done: fbConnected, active: !fbConnected },
                  { label: t('autoPublish.promoEnabledLabel'), done: enabled, active: fbConnected && !enabled },
                  { label: 'Posty publikują się same', done: enabled && fbConnected },
                ]}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </ProfileGate>
  );

  return embedded ? content : <PanelLayout>{content}</PanelLayout>;
}
